import React from 'react';
import {
  Box, Paper, TextField, Button, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, TablePagination, TableSortLabel,
  CircularProgress, Chip, Typography, Autocomplete, ToggleButtonGroup, ToggleButton
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

import Client from '../Client';
import Config from '../ClientConfig';
import BackofficeReportsApi from './backofficeReportsApi';

// ---------- helpers ----------
const pad2 = (n) => String(n).padStart(2, '0');
const today = () => new Date();
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const ymd = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const sameMonth = (d1, d2) => d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth();

const fmt0 = (n) => (n == null || isNaN(n) ? '-' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 }));
const fmt2 = (n) => (n == null || isNaN(n) ? '-' : Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fmtPct1 = (n) => (n == null || isNaN(n) ? '-' : Number(n).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + '%');

function clampSameMonthAndOrder(nextFrom, nextTo) {
  let changed = false;
  let df = nextFrom ? new Date(nextFrom) : null;
  let dt = nextTo ? new Date(nextTo) : null;

  if (df && dt) {
    if (!sameMonth(df, dt)) {
      dt = new Date(df);
      changed = true;
    }

    if (df > dt) {
      dt = new Date(df);
      changed = true;
    }
  }

  const outFrom = df ? ymd(df) : nextFrom;
  const outTo = dt ? ymd(dt) : nextTo;

  return {
    dateFrom: outFrom,
    dateTo: outTo,
    changed,
    note: changed ? 'Adjusted to same month & order' : '',
  };
}

// ---------- API roots ----------
const API_URL_PM_BY_LOCATION = BackofficeReportsApi.getMarginsByProductUrl();
const API_URL_PM_BY_PRODUCT_LOCATIONS = BackofficeReportsApi.getMarginsByProductLocationsUrl();
const API_URL_PRODUCTS = Config.API_URL_PRODUCT + 'list_products/';
const API_URL_LOCATIONS = Config.API_URL_LOCATIONS + 'list_locations/';

// Modes
const MODES = {
  P2PERIODS: 'P2PERIODS',
  L2PERIODS: 'L2PERIODS',
  P2LOCS: 'P2LOCS',
  L2PRODS: 'L2PRODS',
};

// ---------- compact styles ----------
const compactFieldSx = {
  minWidth: 0,
  '& .MuiInputBase-root': {
    minHeight: 34,
    borderRadius: '12px',
  },
  '& .MuiInputBase-input': {
    fontSize: {
      xs: '16px',
      sm: '0.84rem',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: {
      xs: '0.78rem',
      sm: '0.84rem',
    },
  },
};

const dateFieldSx = {
  '& .MuiInputBase-root': {
    height: 34,
    borderRadius: '12px',
  },
  '& input': {
    py: 0.35,
    fontSize: {
      xs: '16px',
      sm: '0.84rem',
    },
  },
};

const compareCellSx = {
  py: { xs: 0.45, sm: 0.65, md: 0.75 },
  px: { xs: 0.45, sm: 0.7, md: 0.9 },
  whiteSpace: 'nowrap',
  fontSize: {
    xs: '0.72rem',
    sm: '0.82rem',
    md: '0.9rem',
  },
  fontVariantNumeric: 'tabular-nums',
};

const compareHeadSx = {
  ...compareCellSx,
  bgcolor: '#f8fafc',
  color: '#1a237e',
  fontWeight: 750,
  lineHeight: 1.1,
};

const comparePaginationSx = {
  flexShrink: 0,
  borderTop: '1px solid rgba(15, 23, 42, 0.06)',
  minHeight: { xs: 38, sm: 50 },
  overflow: 'hidden',
  '& .MuiTablePagination-toolbar': {
    minHeight: { xs: 38, sm: 50 },
    px: { xs: 0.25, sm: 1 },
    flexWrap: 'nowrap',
    gap: { xs: 0.25, sm: 0.5 },
  },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    fontSize: { xs: '0.72rem', sm: '0.86rem' },
    m: 0,
  },
  '& .MuiTablePagination-actions': {
    ml: { xs: 0.25, sm: 1 },
  },
  '& .MuiInputBase-root': {
    fontSize: { xs: '0.72rem', sm: '0.86rem' },
    mr: { xs: 0.25, sm: 1 },
  },
  '& .MuiIconButton-root': {
    p: { xs: 0.35, sm: 0.5 },
  },
};

const compactChipSx = {
  height: { xs: 22, sm: 26 },
  borderRadius: '9px',
  fontWeight: 650,
  fontSize: { xs: '0.66rem', sm: '0.78rem' },
  '& .MuiChip-label': {
    px: { xs: 0.55, sm: 0.8 },
  },
};

const actionButtonSx = {
  height: 32,
  borderRadius: '11px',
  textTransform: 'none',
  fontWeight: 750,
};

function ChipsSummary({ label, totals, fixedLabel }) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 0.4,
        alignItems: 'center',
        flexWrap: 'wrap',
        minWidth: 0,
      }}
    >
      <Chip
        size="small"
        label={<><b>{label}:</b>&nbsp;{fixedLabel}</>}
        sx={{
          ...compactChipSx,
          maxWidth: '100%',
          '& .MuiChip-label': {
            ...compactChipSx['& .MuiChip-label'],
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        }}
      />

      {totals && (
        <>
          <Chip size="small" label={<><b>Qty</b>&nbsp;{fmt0(totals.qty)}</>} sx={compactChipSx} />
          <Chip size="small" label={<><b>Margin</b>&nbsp;{fmt0(totals.margin)}</>} sx={compactChipSx} />
          <Chip size="small" label={<><b>%</b>&nbsp;{fmtPct1(totals.marginPct)}</>} sx={compactChipSx} />
        </>
      )}
    </Box>
  );
}

export default class InventoryMarginCompare extends React.Component {
  state = {
    client: new Client('none'),

    products: [],
    locations: [],
    loadingLookups: true,

    mode: MODES.P2PERIODS,
    delivery: '',
    appClientsOnly: false,

    dateFromA: ymd(startOfMonth(today())),
    dateToA: ymd(today()),
    dateFromB: ymd(startOfMonth(today())),
    dateToB: ymd(today()),

    productFixed: { id: '', label: '(no product)' },
    locationFixed: { id: '', label: '(no location)' },
    productA: { id: '', label: '(no product)' },
    productB: { id: '', label: '(no product)' },
    locationA: { id: '', label: '(no location)' },
    locationB: { id: '', label: '(no location)' },

    leftRows: [],
    rightRows: [],
    leftTotals: null,
    rightTotals: null,
    loading: false,

    page: 0,
    rowsPerPage: -1,
    order: 'asc',
    orderBy: null,
  };

  async componentDidMount() {
    try {
      const [pRes, lRes] = await Promise.all([
        this.state.client.getData(API_URL_PRODUCTS, { isForSale: 1, orderBy_name: true }) || {},
        this.state.client.getData(API_URL_LOCATIONS, { status: 1 }) || {},
      ]);

      const productsRaw = pRes?.products || pRes?.results || pRes || [];
      const locationsRaw = lRes?.locations || lRes?.results || lRes || [];

      const products = productsRaw.map(p => ({
        id: String(p.iref || p.id),
        label: p.name,
      }));

      const locations = locationsRaw
        .map(l => ({
          id: String(l.iref || l.id),
          label: l.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

      this.setState({ products, locations, loadingLookups: false });
    } catch {
      this.setState({ loadingLookups: false });
    }
  }

  normalizeRows = (raw) => {
    return (raw || []).map(r => {
      const productName = r.productName ?? r.product__name ?? r.product_name ?? 'Product';
      const locationName = r.locationName ?? r.location__name ?? r.location_name ?? 'Location';
      const qty = Number(r.qty || 0);
      const gross = Number(r.gross || 0);
      const net = Number(r.net || (gross - Number(r.vat || 0)));
      const margin = Number(r.margin != null ? r.margin : (net - Number(r.cogs || 0)));
      const marginPct = net ? (margin / net * 100.0) : (r.marginPct != null ? Number(r.marginPct) : null);
      const unitPrice = qty ? (gross / qty) : null;
      const unitMargin = qty ? (margin / qty) : null;

      return {
        productName,
        locationName,
        qty,
        gross,
        margin,
        unitPrice,
        unitMargin,
        marginPct,
      };
    });
  };

  buildQueries = () => {
    const {
      mode,
      delivery,
      appClientsOnly,
      dateFromA,
      dateToA,
      dateFromB,
      dateToB,
      productFixed,
      locationFixed,
      productA,
      productB,
      locationA,
      locationB,
    } = this.state;

    const del = (delivery === '0' || delivery === '1') ? { delivery } : {};
    const clients = appClientsOnly ? { appClientsOnly: 1 } : {};

    if (mode === MODES.P2PERIODS) {
      const left = {
        url: API_URL_PM_BY_LOCATION,
        params: { dateFrom: dateFromA, dateTo: dateToA, location: locationFixed.id, ...del, ...clients },
      };

      const right = {
        url: API_URL_PM_BY_LOCATION,
        params: { dateFrom: dateFromB, dateTo: dateToB, location: locationFixed.id, ...del, ...clients },
      };

      return {
        left,
        right,
        key: 'productName',
        leftLabel: `Loc: ${locationFixed.label} @ ${dateFromA}—${dateToA}`,
        rightLabel: `Loc: ${locationFixed.label} @ ${dateFromB}—${dateToB}`,
      };
    }

    if (mode === MODES.L2PERIODS) {
      const left = {
        url: API_URL_PM_BY_PRODUCT_LOCATIONS,
        params: { dateFrom: dateFromA, dateTo: dateToA, product: productFixed.id, ...del, ...clients },
      };

      const right = {
        url: API_URL_PM_BY_PRODUCT_LOCATIONS,
        params: { dateFrom: dateFromB, dateTo: dateToB, product: productFixed.id, ...del, ...clients },
      };

      return {
        left,
        right,
        key: 'locationName',
        leftLabel: `Prod: ${productFixed.label} @ ${dateFromA}—${dateToA}`,
        rightLabel: `Prod: ${productFixed.label} @ ${dateFromB}—${dateToB}`,
      };
    }

    if (mode === MODES.P2LOCS) {
      const left = {
        url: API_URL_PM_BY_LOCATION,
        params: { dateFrom: dateFromA, dateTo: dateToA, location: locationA.id, ...del, ...clients },
      };

      const right = {
        url: API_URL_PM_BY_LOCATION,
        params: { dateFrom: dateFromA, dateTo: dateToA, location: locationB.id, ...del, ...clients },
      };

      return {
        left,
        right,
        key: 'productName',
        leftLabel: `Loc: ${locationA.label} @ ${dateFromA}—${dateToA}`,
        rightLabel: `Loc: ${locationB.label} @ ${dateFromA}—${dateToA}`,
      };
    }

    if (mode === MODES.L2PRODS) {
      const left = {
        url: API_URL_PM_BY_PRODUCT_LOCATIONS,
        params: { dateFrom: dateFromA, dateTo: dateToA, product: productA.id, ...del, ...clients },
      };

      const right = {
        url: API_URL_PM_BY_PRODUCT_LOCATIONS,
        params: { dateFrom: dateFromA, dateTo: dateToA, product: productB.id, ...del, ...clients },
      };

      return {
        left,
        right,
        key: 'locationName',
        leftLabel: `Prod: ${productA.label} @ ${dateFromA}—${dateToA}`,
        rightLabel: `Prod: ${productB.label} @ ${dateFromA}—${dateToA}`,
      };
    }

    return null;
  };

  apply = async () => {
    const q = this.buildQueries();

    if (!q) return;

    const { client } = this.state;

    this.setState({
      loading: true,
      leftRows: [],
      rightRows: [],
      leftTotals: null,
      rightTotals: null,
      page: 0,
    });

    try {
      const [leftData, rightData] = await Promise.all([
        client.getData(q.left.url, q.left.params) || {},
        client.getData(q.right.url, q.right.params) || {},
      ]);

      const leftRows = this.normalizeRows(leftData.rows || []);
      const rightRows = this.normalizeRows(rightData.rows || []);

      const calcTotals = (data, rows) => {
        const t = rows.reduce((a, r) => {
          a.qty += r.qty;
          a.gross += r.gross;
          a.margin += r.margin;
          return a;
        }, {
          qty: 0,
          gross: 0,
          margin: 0,
        });

        return {
          qty: t.qty,
          gross: t.gross,
          margin: t.margin,
          marginPct: (
            data?.totals
            && (
              data.totals.marginPct != null
                ? Number(data.totals.marginPct)
                : Number(data.totals.net) > 0
                  ? (Number(data.totals.margin) / Number(data.totals.net) * 100)
                  : null
            )
          ),
        };
      };

      const leftTotals = calcTotals(leftData, leftRows);
      const rightTotals = calcTotals(rightData, rightRows);

      this.setState({
        leftRows,
        rightRows,
        leftTotals,
        rightTotals,
        loading: false,
        leftLabel: q.leftLabel,
        rightLabel: q.rightLabel,
        alignKey: q.key,
      });
    } catch (e) {
      this.setState({ loading: false });
    }
  };

  buildAligned = () => {
    const { leftRows, rightRows, alignKey } = this.state;
    const key = alignKey || 'productName';

    const mapL = new Map(leftRows.map(r => [r[key], r]));
    const mapR = new Map(rightRows.map(r => [r[key], r]));
    const keys = new Set([...mapL.keys(), ...mapR.keys()]);

    const rows = Array.from(keys).map(k => ({
      key: k,
      left: mapL.get(k) || {},
      right: mapR.get(k) || {},
    }));

    rows.sort((a, b) => String(a.key || '').localeCompare(String(b.key || '')));

    return rows;
  };

  handleRequestSort = (property) => {
    this.setState((state) => ({
      order: state.orderBy === property && state.order === 'asc' ? 'desc' : 'asc',
      orderBy: property,
      page: 0,
    }));
  };

  canApply() {
    const s = this.state;

    const validDates = (df, dt) =>
      !!df && !!dt && (new Date(df) <= new Date(dt)) && sameMonth(new Date(df), new Date(dt));

    switch (s.mode) {
      case MODES.P2PERIODS:
        return !!s.locationFixed?.id && validDates(s.dateFromA, s.dateToA) && validDates(s.dateFromB, s.dateToB);

      case MODES.L2PERIODS:
        return !!s.productFixed?.id && validDates(s.dateFromA, s.dateToA) && validDates(s.dateFromB, s.dateToB);

      case MODES.P2LOCS:
        return !!s.locationA?.id && !!s.locationB?.id && validDates(s.dateFromA, s.dateToA);

      case MODES.L2PRODS:
        return !!s.productA?.id && !!s.productB?.id && validDates(s.dateFromA, s.dateToA);

      default:
        return false;
    }
  }

  exportCSV = () => {
    const rows = this.buildAligned();

    const header = [
      'Item',
      'qtyA',
      'unitPriceA',
      'marginA',
      'unitMarginA',
      'marginPctA',
      'qtyB',
      'unitPriceB',
      'marginB',
      'unitMarginB',
      'marginPctB',
    ];

    const modeLabelMap = {
      P2PERIODS: 'Products × 2 periods',
      L2PERIODS: 'Locations × 2 periods',
      P2LOCS: 'Products × 2 locations',
      L2PRODS: 'Locations × 2 products',
    };

    const modeLabel = modeLabelMap[this.state.mode] || this.state.mode || '';
    const deliveryLabel = this.state.delivery === '' ? 'Any' : this.state.delivery === '1' ? 'Yes' : 'No';

    const meta = [
      ['Mode', modeLabel],
      ['Delivery', deliveryLabel],
      ['Clients', this.state.appClientsOnly ? 'App Clients' : 'All'],
      ['Left (A)', this.state.leftLabel || ''],
      ['Right (B)', this.state.rightLabel || ''],
      ['Generated', new Date().toISOString()],
    ];

    const dataRows = rows.map(r => [
      r.key,
      r.left?.qty ?? '',
      r.left?.unitPrice ?? '',
      r.left?.margin ?? '',
      r.left?.unitMargin ?? '',
      r.left?.marginPct ?? '',
      r.right?.qty ?? '',
      r.right?.unitPrice ?? '',
      r.right?.margin ?? '',
      r.right?.unitMargin ?? '',
      r.right?.marginPct ?? '',
    ]);

    const csvRows = [...meta, [''], header, ...dataRows];

    const csv = csvRows
      .map(r => Array.isArray(r)
        ? r.map(x => `"${String(x ?? '').replace(/"/g, '""')}"`).join(',')
        : '')
      .join('\r\n');

    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');

    a.style.display = 'none';
    a.href = url;
    a.setAttribute('download', 'margin_compare.csv');

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  renderSmartFilters() {
    const {
      mode,
      delivery,
      appClientsOnly,
      dateFromA,
      dateToA,
      dateFromB,
      dateToB,
      productFixed,
      locationFixed,
      productA,
      productB,
      locationA,
      locationB,
      products,
      locations,
    } = this.state;

    const rows = this.buildAligned();

    const onDateAFrom = (v) => {
      const adj = clampSameMonthAndOrder(v, dateToA);
      this.setState({ dateFromA: adj.dateFrom, dateToA: adj.dateTo });
    };

    const onDateATo = (v) => {
      const adj = clampSameMonthAndOrder(dateFromA, v);
      this.setState({ dateFromA: adj.dateFrom, dateToA: adj.dateTo });
    };

    const onDateBFrom = (v) => {
      const adj = clampSameMonthAndOrder(v, dateToB);
      this.setState({ dateFromB: adj.dateFrom, dateToB: adj.dateTo });
    };

    const onDateBTo = (v) => {
      const adj = clampSameMonthAndOrder(dateFromB, v);
      this.setState({ dateFromB: adj.dateFrom, dateToB: adj.dateTo });
    };

    const leftShade = 'rgba(25,118,210,0.06)';
    const rightShade = 'rgba(46,125,50,0.06)';

    return (
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 0.5, sm: 0.75 },
          mb: 0.75,
          borderRadius: '14px',
          borderColor: 'rgba(15, 23, 42, 0.08)',
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        <Grid container spacing={0.75} alignItems="center">
          <Grid item xs={12} md={6}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={mode}
              onChange={(_e, v) => v && this.setState({ mode: v })}
              sx={{
                width: '100%',
                overflowX: 'auto',
                display: 'flex',
                '& .MuiToggleButton-root': {
                  flexShrink: 0,
                  height: 32,
                  px: { xs: 0.8, sm: 1.1 },
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: { xs: '0.72rem', sm: '0.8rem' },
                  whiteSpace: 'nowrap',
                },
              }}
            >
              <ToggleButton value={MODES.P2PERIODS} title="Products by 2 periods">
                Prod × 2 periods
              </ToggleButton>
              <ToggleButton value={MODES.L2PERIODS} title="Locations by 2 periods">
                Loc × 2 periods
              </ToggleButton>
              <ToggleButton value={MODES.P2LOCS} title="Products by 2 locations">
                Prod × 2 locations
              </ToggleButton>
              <ToggleButton value={MODES.L2PRODS} title="Locations by 2 products">
                Loc × 2 products
              </ToggleButton>
            </ToggleButtonGroup>

            <Box sx={{ mt: 0.5, mb: { xs: 0.5, md: 1 } }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  whiteSpace: { xs: 'nowrap', md: 'normal' },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: { xs: '0.7rem', sm: '0.76rem' },
                }}
              >
                {mode === MODES.P2PERIODS && 'Products for one location across two periods.'}
                {mode === MODES.L2PERIODS && 'Locations for one product across two periods.'}
                {mode === MODES.P2LOCS && 'Products for one period across two locations.'}
                {mode === MODES.L2PRODS && 'Locations for one period across two products.'}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6} md={2}>
            <Autocomplete
              size="small"
              options={[
                { id: '', label: 'Delivery: Any' },
                { id: '0', label: 'Delivery: No' },
                { id: '1', label: 'Delivery: Yes' },
              ]}
              value={[
                { id: '', label: 'Delivery: Any' },
                { id: '0', label: 'Delivery: No' },
                { id: '1', label: 'Delivery: Yes' },
              ].find(x => x.id === (delivery ?? ''))}
              onChange={(_e, v) => this.setState({ delivery: v?.id ?? '' })}
              isOptionEqualToValue={(opt, val) => (opt?.id ?? '') === (val?.id ?? '')}
              getOptionLabel={(o) => o?.label || ''}
              renderInput={(params) => (
                <TextField {...params} label="Delivery" size="small" fullWidth />
              )}
              sx={compactFieldSx}
            />
          </Grid>

          <Grid item xs={6} md={2}>
            <Autocomplete
              size="small"
              disableClearable
              options={[
                { id: false, label: 'All' },
                { id: true, label: 'App Clients' },
              ]}
              value={[
                { id: false, label: 'All' },
                { id: true, label: 'App Clients' },
              ].find(x => x.id === appClientsOnly)}
              onChange={(_e, v) => this.setState({ appClientsOnly: v?.id === true })}
              isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
              getOptionLabel={(o) => o?.label || ''}
              renderInput={(params) => (
                <TextField {...params} label="Clients" size="small" fullWidth />
              )}
              sx={compactFieldSx}
            />
          </Grid>

          <Grid
            item
            xs={12}
            md={2}
            sx={{
              display: 'flex',
              justifyContent: { xs: 'stretch', md: 'flex-end' },
              gap: 0.5,
              flexWrap: 'wrap',
              '& .MuiButton-root': {
                ...actionButtonSx,
                flex: { xs: '1 1 auto', md: '0 0 auto' },
                minWidth: { xs: 56, md: 'auto' },
              },
            }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={this.exportCSV}
              disabled={!rows.length}
              startIcon={<DownloadIcon />}
            >
              CSV
            </Button>

            <Button
              size="small"
              variant="outlined"
              onClick={() => this.setState({
                dateFromA: ymd(startOfMonth(today())),
                dateToA: ymd(today()),
                dateFromB: ymd(startOfMonth(today())),
                dateToB: ymd(today()),
              })}
            >
              TM
            </Button>

            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const d = today();
                d.setMonth(d.getMonth() - 1);

                this.setState({
                  dateFromA: ymd(startOfMonth(d)),
                  dateToA: ymd(endOfMonth(d)),
                  dateFromB: ymd(startOfMonth(d)),
                  dateToB: ymd(endOfMonth(d)),
                });
              }}
            >
              LM
            </Button>

            <Button
              size="small"
              variant="contained"
              onClick={this.apply}
              disabled={!this.canApply()}
            >
              Apply
            </Button>

            <Button
              size="small"
              variant="text"
              onClick={() => this.setState({
                leftRows: [],
                rightRows: [],
                leftTotals: null,
                rightTotals: null,
                page: 0,
              })}
            >
              Reset
            </Button>
          </Grid>

          {mode === MODES.P2PERIODS && (
            <>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  size="small"
                  options={[{ id: '', label: '(no location)' }, ...locations]}
                  value={[{ id: '', label: '(no location)' }, ...locations].find(x => x.id === (locationFixed?.id ?? ''))}
                  onChange={(_e, v) => this.setState({ locationFixed: v || { id: '', label: '(no location)' } })}
                  isOptionEqualToValue={(opt, val) => (opt?.id ?? '') === (val?.id ?? '')}
                  getOptionLabel={(o) => o?.label || ''}
                  renderInput={(params) => (
                    <TextField {...params} label="Location" size="small" fullWidth />
                  )}
                  sx={compactFieldSx}
                />
              </Grid>

              <Grid item xs={6} md={2} sx={{ backgroundColor: leftShade, borderRadius: 1 }}>
                <TextField
                  label="From A"
                  type="date"
                  size="small"
                  value={dateFromA}
                  onChange={e => onDateAFrom(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={dateFieldSx}
                />
              </Grid>

              <Grid item xs={6} md={2} sx={{ backgroundColor: leftShade, borderRadius: 1 }}>
                <TextField
                  label="To A"
                  type="date"
                  size="small"
                  value={dateToA}
                  onChange={e => onDateATo(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={dateFieldSx}
                />
              </Grid>

              <Grid item xs={6} md={2} sx={{ backgroundColor: rightShade, borderRadius: 1 }}>
                <TextField
                  label="From B"
                  type="date"
                  size="small"
                  value={dateFromB}
                  onChange={e => onDateBFrom(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={dateFieldSx}
                />
              </Grid>

              <Grid item xs={6} md={2} sx={{ backgroundColor: rightShade, borderRadius: 1 }}>
                <TextField
                  label="To B"
                  type="date"
                  size="small"
                  value={dateToB}
                  onChange={e => onDateBTo(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={dateFieldSx}
                />
              </Grid>
            </>
          )}

          {mode === MODES.L2PERIODS && (
            <>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  size="small"
                  options={[{ id: '', label: '(no product)' }, ...products]}
                  value={[{ id: '', label: '(no product)' }, ...products].find(x => x.id === (productFixed?.id ?? ''))}
                  onChange={(_e, v) => this.setState({ productFixed: v || { id: '', label: '(no product)' } })}
                  isOptionEqualToValue={(opt, val) => (opt?.id ?? '') === (val?.id ?? '')}
                  getOptionLabel={(o) => o?.label || ''}
                  renderInput={(params) => (
                    <TextField {...params} label="Product" size="small" fullWidth />
                  )}
                  sx={compactFieldSx}
                />
              </Grid>

              <Grid item xs={6} md={2} sx={{ backgroundColor: leftShade, borderRadius: 1 }}>
                <TextField
                  label="From A"
                  type="date"
                  size="small"
                  value={dateFromA}
                  onChange={e => onDateAFrom(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={dateFieldSx}
                />
              </Grid>

              <Grid item xs={6} md={2} sx={{ backgroundColor: leftShade, borderRadius: 1 }}>
                <TextField
                  label="To A"
                  type="date"
                  size="small"
                  value={dateToA}
                  onChange={e => onDateATo(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={dateFieldSx}
                />
              </Grid>

              <Grid item xs={6} md={2} sx={{ backgroundColor: rightShade, borderRadius: 1 }}>
                <TextField
                  label="From B"
                  type="date"
                  size="small"
                  value={dateFromB}
                  onChange={e => onDateBFrom(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={dateFieldSx}
                />
              </Grid>

              <Grid item xs={6} md={2} sx={{ backgroundColor: rightShade, borderRadius: 1 }}>
                <TextField
                  label="To B"
                  type="date"
                  size="small"
                  value={dateToB}
                  onChange={e => onDateBTo(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={dateFieldSx}
                />
              </Grid>
            </>
          )}

          {mode === MODES.P2LOCS && (
            <>
              <Grid item xs={12} md={4} sx={{ backgroundColor: leftShade, borderRadius: 1 }}>
                <Autocomplete
                  size="small"
                  options={[{ id: '', label: '(no location)' }, ...locations]}
                  value={[{ id: '', label: '(no location)' }, ...locations].find(x => x.id === (locationA?.id ?? ''))}
                  onChange={(_e, v) => this.setState({ locationA: v || { id: '', label: '(no location)' } })}
                  isOptionEqualToValue={(opt, val) => (opt?.id ?? '') === (val?.id ?? '')}
                  getOptionLabel={(o) => o?.label || ''}
                  renderInput={(params) => (
                    <TextField {...params} label="Location A" size="small" fullWidth />
                  )}
                  sx={compactFieldSx}
                />
              </Grid>

              <Grid item xs={12} md={4} sx={{ backgroundColor: rightShade, borderRadius: 1 }}>
                <Autocomplete
                  size="small"
                  options={[{ id: '', label: '(no location)' }, ...locations]}
                  value={[{ id: '', label: '(no location)' }, ...locations].find(x => x.id === (locationB?.id ?? ''))}
                  onChange={(_e, v) => this.setState({ locationB: v || { id: '', label: '(no location)' } })}
                  isOptionEqualToValue={(opt, val) => (opt?.id ?? '') === (val?.id ?? '')}
                  getOptionLabel={(o) => o?.label || ''}
                  renderInput={(params) => (
                    <TextField {...params} label="Location B" size="small" fullWidth />
                  )}
                  sx={compactFieldSx}
                />
              </Grid>

              <Grid item xs={6} md={2}>
                <TextField
                  label="From"
                  type="date"
                  size="small"
                  value={dateFromA}
                  onChange={e => {
                    const adj = clampSameMonthAndOrder(e.target.value, dateToA);
                    this.setState({ dateFromA: adj.dateFrom, dateToA: adj.dateTo });
                  }}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={dateFieldSx}
                />
              </Grid>

              <Grid item xs={6} md={2}>
                <TextField
                  label="To"
                  type="date"
                  size="small"
                  value={dateToA}
                  onChange={e => {
                    const adj = clampSameMonthAndOrder(dateFromA, e.target.value);
                    this.setState({ dateFromA: adj.dateFrom, dateToA: adj.dateTo });
                  }}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={dateFieldSx}
                />
              </Grid>
            </>
          )}

          {mode === MODES.L2PRODS && (
            <>
              <Grid item xs={12} md={4} sx={{ backgroundColor: leftShade, borderRadius: 1 }}>
                <Autocomplete
                  size="small"
                  options={[{ id: '', label: '(no product)' }, ...products]}
                  value={[{ id: '', label: '(no product)' }, ...products].find(x => x.id === (productA?.id ?? ''))}
                  onChange={(_e, v) => this.setState({ productA: v || { id: '', label: '(no product)' } })}
                  isOptionEqualToValue={(opt, val) => (opt?.id ?? '') === (val?.id ?? '')}
                  getOptionLabel={(o) => o?.label || ''}
                  renderInput={(params) => (
                    <TextField {...params} label="Product A" size="small" fullWidth />
                  )}
                  sx={compactFieldSx}
                />
              </Grid>

              <Grid item xs={12} md={4} sx={{ backgroundColor: rightShade, borderRadius: 1 }}>
                <Autocomplete
                  size="small"
                  options={[{ id: '', label: '(no product)' }, ...products]}
                  value={[{ id: '', label: '(no product)' }, ...products].find(x => x.id === (productB?.id ?? ''))}
                  onChange={(_e, v) => this.setState({ productB: v || { id: '', label: '(no product)' } })}
                  isOptionEqualToValue={(opt, val) => (opt?.id ?? '') === (val?.id ?? '')}
                  getOptionLabel={(o) => o?.label || ''}
                  renderInput={(params) => (
                    <TextField {...params} label="Product B" size="small" fullWidth />
                  )}
                  sx={compactFieldSx}
                />
              </Grid>

              <Grid item xs={6} md={2}>
                <TextField
                  label="From"
                  type="date"
                  size="small"
                  value={dateFromA}
                  onChange={e => {
                    const adj = clampSameMonthAndOrder(e.target.value, dateToA);
                    this.setState({ dateFromA: adj.dateFrom, dateToA: adj.dateTo });
                  }}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={dateFieldSx}
                />
              </Grid>

              <Grid item xs={6} md={2}>
                <TextField
                  label="To"
                  type="date"
                  size="small"
                  value={dateToA}
                  onChange={e => {
                    const adj = clampSameMonthAndOrder(dateFromA, e.target.value);
                    this.setState({ dateFromA: adj.dateFrom, dateToA: adj.dateTo });
                  }}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={dateFieldSx}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Paper>
    );
  }

  render() {
    const {
      leftTotals, rightTotals, page, rowsPerPage, leftLabel, rightLabel, alignKey,
      order, orderBy,
    } = this.state;
    const rows = this.buildAligned();
    const sortedRows = orderBy ? [...rows].sort((a, b) => {
      let aValue;
      let bValue;

      if (orderBy === 'key') {
        aValue = String(a.key || '').toLocaleLowerCase();
        bValue = String(b.key || '').toLocaleLowerCase();
      } else {
        const [side, field] = orderBy.split('.');
        aValue = Number(a[side]?.[field] ?? 0);
        bValue = Number(b[side]?.[field] ?? 0);
      }

      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    }) : rows;
    const sliceStart = page * rowsPerPage;
    const pageRows = rowsPerPage > 0
      ? sortedRows.slice(sliceStart, sliceStart + rowsPerPage)
      : sortedRows;

    const sortLabel = (property, label) => (
      <TableSortLabel
        active={orderBy === property}
        direction={orderBy === property ? order : 'asc'}
        onClick={() => this.handleRequestSort(property)}
      >
        {label}
      </TableSortLabel>
    );

    const leftShade = 'rgba(25,118,210,0.06)';
    const rightShade = 'rgba(46,125,50,0.06)';

    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          minHeight: 0,
          maxHeight: '100%',
          minWidth: 0,
          overflow: 'hidden',
          bgcolor: 'background.default',
          p: {
            xs: 0.5,
            sm: 0.85,
            md: 1.1,
          },
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {this.renderSmartFilters()}

        <Paper
          elevation={0}
          sx={{
            flexShrink: 0,
            my: 0.5,
            px: { xs: 0.5, sm: 0.85 },
            py: 0.5,
            borderRadius: '12px',
            border: '1px solid rgba(15, 23, 42, 0.06)',
            overflow: 'hidden',
          }}
        >
          <Grid container alignItems="center" spacing={0.5}>
            <Grid item xs={12} md={5.5}>
              <ChipsSummary label="A" totals={leftTotals} fixedLabel={leftLabel || ''} />
            </Grid>

            <Grid item xs={12} md={1} sx={{ display: { xs: 'none', md: 'block' } }} />

            <Grid
              item
              xs={12}
              md={5.5}
              sx={{
                display: 'flex',
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
              }}
            >
              <ChipsSummary label="B" totals={rightTotals} fixedLabel={rightLabel || ''} />
            </Grid>
          </Grid>
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '14px',
            borderColor: 'rgba(15, 23, 42, 0.08)',
          }}
        >
          <TableContainer
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              width: '100%',
            }}
          >
            <Table
              size="small"
              stickyHeader
              sx={{
                minWidth: {
                  xs: 1180,
                  sm: 1220,
                  md: '100%',
                },
                '& .MuiTableCell-stickyHeader': {
                  backgroundColor: '#f8fafc',
                  zIndex: 3,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...compareHeadSx, minWidth: 220 }}>
                    {sortLabel('key', alignKey === 'locationName' ? 'Location' : 'Product')}
                  </TableCell>
                  <TableCell align="right" sx={compareHeadSx}>{sortLabel('left.qty', 'Qty A')}</TableCell>
                  <TableCell align="right" sx={compareHeadSx}>{sortLabel('left.unitPrice', 'Unit Price A')}</TableCell>
                  <TableCell align="right" sx={compareHeadSx}>{sortLabel('left.margin', 'Margin A')}</TableCell>
                  <TableCell align="right" sx={compareHeadSx}>{sortLabel('left.unitMargin', 'Unit Margin A')}</TableCell>
                  <TableCell align="right" sx={compareHeadSx}>{sortLabel('left.marginPct', '% A')}</TableCell>
                  <TableCell sx={compareHeadSx} />
                  <TableCell align="right" sx={compareHeadSx}>{sortLabel('right.qty', 'Qty B')}</TableCell>
                  <TableCell align="right" sx={compareHeadSx}>{sortLabel('right.unitPrice', 'Unit Price B')}</TableCell>
                  <TableCell align="right" sx={compareHeadSx}>{sortLabel('right.margin', 'Margin B')}</TableCell>
                  <TableCell align="right" sx={compareHeadSx}>{sortLabel('right.unitMargin', 'Unit Margin B')}</TableCell>
                  <TableCell align="right" sx={compareHeadSx}>{sortLabel('right.marginPct', '% B')}</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {this.state.loading && (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={compareCellSx}>
                      <CircularProgress size={20} />
                    </TableCell>
                  </TableRow>
                )}

                {!this.state.loading && !pageRows.length && (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={compareCellSx}>
                      No data
                    </TableCell>
                  </TableRow>
                )}

                {pageRows.map((r, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell sx={compareCellSx}>{r.key}</TableCell>
                    <TableCell align="right" sx={{ ...compareCellSx, backgroundColor: leftShade }}>{fmt0(r.left?.qty)}</TableCell>
                    <TableCell align="right" sx={{ ...compareCellSx, backgroundColor: leftShade }}>{fmt2(r.left?.unitPrice)}</TableCell>
                    <TableCell align="right" sx={{ ...compareCellSx, backgroundColor: leftShade }}>{fmt0(r.left?.margin)}</TableCell>
                    <TableCell align="right" sx={{ ...compareCellSx, backgroundColor: leftShade }}>{fmt2(r.left?.unitMargin)}</TableCell>
                    <TableCell align="right" sx={{ ...compareCellSx, backgroundColor: leftShade }}>{fmtPct1(r.left?.marginPct)}</TableCell>

                    <TableCell sx={compareCellSx} />

                    <TableCell align="right" sx={{ ...compareCellSx, backgroundColor: rightShade }}>{fmt0(r.right?.qty)}</TableCell>
                    <TableCell align="right" sx={{ ...compareCellSx, backgroundColor: rightShade }}>{fmt2(r.right?.unitPrice)}</TableCell>
                    <TableCell align="right" sx={{ ...compareCellSx, backgroundColor: rightShade }}>{fmt0(r.right?.margin)}</TableCell>
                    <TableCell align="right" sx={{ ...compareCellSx, backgroundColor: rightShade }}>{fmt2(r.right?.unitMargin)}</TableCell>
                    <TableCell align="right" sx={{ ...compareCellSx, backgroundColor: rightShade }}>{fmtPct1(r.right?.marginPct)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={rows.length}
            page={page}
            onPageChange={(_e, p) => this.setState({ page: p })}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => this.setState({ rowsPerPage: Number(e.target.value), page: 0 })}
            rowsPerPageOptions={[10, 25, { value: -1, label: 'All' }]}
            sx={comparePaginationSx}
          />
        </Paper>
      </Box>
    );
  }
}
